<!-- GeneralDialog.vue -->
<!-- 
    FUTURE: If the user scrolls past the edge of the viewport, the page can scroll but  
            the dialog is frozen.  We might want to consider adding a 'scroll' event handler to re-compute dialogDragLimits
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Icon from './Icon.vue';
import { IconTypes, DisplayStyle, HoverCursorTypes, NonModalDisplayMode } from '../Enums/UiEnums';
import { hasSlot } from '../lib/VueHelpers'
import { timeout } from '../lib/ChoiceUtils';
import { viewPortSize } from '../lib/WindowUtils';
import { ICartesianCoord } from '../lib/Spacial/Cartesian';
import { cssValue, ICssLength } from '../lib/CssHelpers';

    const props = withDefaults(defineProps<{
        show?: boolean,
        isModal?: boolean,
        showSectionBreaks?: boolean,
        footerAlignEnd?: boolean,
        closeIconTitle?: string,
        closeModalOnClickOutside?: boolean,  // TODO
        closeOnEscapeKey?: boolean,
        showCloseButton?: boolean,
        nonModalDisplayMode?: NonModalDisplayMode,
        allowDragging: boolean,
        width?: ICssLength,
        height?: ICssLength        
    }>(), {
        show: false,
        isModal: true,
        showSectionBreaks: true,
        footerAlignEnd: true,
        closeIconTitle: "Close Window",
        closeModalOnClickOutside: true,
        closeOnEscapeKey: true,
        showCloseButton: true,
        allowDragging: true
    });

    const emit = defineEmits<{
        (e: "show") : void, 
        (e: "shown"): void, 
        (e: "hidden"): void,
        (e: "update:show", currentValue: boolean): void
    }>();

    const theDialog = ref<HTMLDialogElement>();
    const titleDiv = ref<HTMLElement>()

    const highestZIndex = ref<number>(100);

    let isDragging: boolean = false;
    let mouseStartPos: ICartesianCoord | undefined;
    let dialogDragLimits: { XMin: number, XMax: number, YMin: number, YMax: number } | undefined;
    

    onMounted(() =>{
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        if (props.show)
            showDialog();
    });
    onBeforeUnmount(()=>{
        // If the modal is still showing, remove the event lister intended to listern for clicks outside the modal
        if (props.show)
        {
            document.removeEventListener('click', onClickDocument);
        }

        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);        
    });

    // Methods
    const emitShow = () =>{ emit("show"); }
    const emitShown = () =>{ emit("shown"); emitUpdateShowPropValue(true); }
    const emitHidden = () =>{ emit("hidden"); emitUpdateShowPropValue(false); }
    const emitUpdateShowPropValue = (currentValue: boolean) => {  emit("update:show", currentValue); }

    watch(() => props.show, async (newVal, oldVal) => {
        // Show the dialog?
        if (true === newVal && false === oldVal)
        {
            await showDialog();
        }
        else if (false === newVal && true === oldVal)
        {
            document.removeEventListener('click', onClickDocument);
            hideDialog();
        }
    });

    async function showDialog() {
        emitShow();
        await nextTick();
        if (props.isModal)
            theDialog.value?.showModal();
        else
            theDialog.value?.show();

        // Put the window at the top of the z-index stack
        highestZIndex.value = getHighestZIndex();

        // Position the non-modal
        switch(props.nonModalDisplayMode ?? NonModalDisplayMode.TopCentered)
        {
            case NonModalDisplayMode.TopCentered:
            setModalPosition(undefined, (window.innerHeight /10) + window.scrollY);
            break;

            default:
            alert(`Missing handler for NonModalDisplayMode: ${props.nonModalDisplayMode}`);
            break;
        }

        document.addEventListener("keydown", listenForEscape);

        // Avoid a CTA click action closing the dialog right away
        await timeout(10);
        document.addEventListener('click', onClickDocument);

        emitShown();
    }

    function hideDialog() {
        theDialog.value?.close();
        document.removeEventListener("keydown", listenForEscape);
        emitHidden();
    }

    function listenForEscape(evt: KeyboardEvent)
    {
        if ("Escape" === evt.key)
        {
            if (props.closeOnEscapeKey)
            {
                hideDialog();
            }
        }
    }

    const hasFooter = computed(() => hasSlot("footer"));

    function onTitleMouseDown(evt: MouseEvent)
    {
        if(props.allowDragging)
            isDragging = true;

        const dialog = theDialog.value;
        if (!dialog)
        {
            alert("Invalid dialog");
            return;
        }

        mouseStartPos = {X: evt.clientX, Y: evt.clientY };
        const dialogRect = dialog.getBoundingClientRect();

        const viewportSize = viewPortSize();

        dialogDragLimits = { 
            XMin: Math.ceil(mouseStartPos.X - dialogRect.left), 
            XMax: Math.floor(viewportSize.width - (dialogRect.right - mouseStartPos.X)),  
            YMin: Math.ceil(mouseStartPos.Y - dialogRect.top),
            YMax: Math.floor(viewportSize.height - (dialogRect.bottom - mouseStartPos.Y))
        };

    }

    function onMouseMove(evt: MouseEvent)
    {
        if (!isDragging)
            return;
        if(!props.allowDragging)
            return;
        const dialog = theDialog.value;
        if (!dialog)
        {
            alert("Invalid dialog");
            return;
        }

        if (props.isModal)
        {
            const deltaX = ((evt.clientX) - (mouseStartPos?.X ?? 0)) * 2;
            const deltaY = ((evt.clientY) - (mouseStartPos?.Y ?? 0)) * 2;

            // Update mouse position for next call
            mouseStartPos = { X: evt.clientX, Y: evt.clientY };

            const pos = getCurrentDialogPos();
            if (!pos)
            {
                dialog.style.left = deltaX + 'px';
                dialog.style.top = deltaY + 'px';
            }
            else
            {                    
                // Nudge window
                dialog.style.left = (pos.X + deltaX) + 'px';
                dialog.style.top = (pos.Y + deltaY) + 'px';
            }
        }
        else
        {
            const deltaX = (evt.clientX) - (mouseStartPos?.X ?? 0);
            const deltaY = (evt.clientY) - (mouseStartPos?.Y ?? 0);

            const initialDialogPosition = dialog.getBoundingClientRect();
            const tempMouseStartPos = { X: evt.clientX, Y: evt.clientY };

            // Limit the dialog so it can't move right of the window scroll limit
            if (rejectNewDialogPosition(tempMouseStartPos))
            {
                return;
            }
            mouseStartPos = tempMouseStartPos;


            const pos = getCurrentDialogPos();
            if (!pos)
            {
                dialog.style.left = (window.scrollX + (deltaX * 2)) + 'px';
                dialog.style.top = ((initialDialogPosition?.top ?? 0) + window.scrollY + deltaY) + 'px';
            }
            else
            {
                // Nudge window
                dialog.style.left = (pos.X + deltaX*2) + 'px';
                dialog.style.top = (pos.Y + deltaY) + 'px';
            }
        }
    }

    function rejectNewDialogPosition(tempMouseStartPos: ICartesianCoord): boolean
    {
        if (tempMouseStartPos && dialogDragLimits)
        {
            if (tempMouseStartPos?.X >= dialogDragLimits.XMax)
                return true;
            if (tempMouseStartPos?.X <= dialogDragLimits.XMin)
                return true;

            if (tempMouseStartPos?.Y <= dialogDragLimits.YMin)
                return true;            
            if (tempMouseStartPos?.Y >= dialogDragLimits.YMax)
                return true;
        }

        return false;
    }

    function onMouseUp()
    {
        isDragging = false;
    }

    // List to see if the user clicked outside of the modal
    function onClickDocument(evt: MouseEvent)
    {
        // Do we need to monitor for non-modal clicks outside the modal?
        if (!props.closeModalOnClickOutside || !props.isModal)
            return; 
        
        const modalRect = theDialog.value?.getBoundingClientRect();
        if (!modalRect)
            return;

        // if we're in the dialog, ignore the click
        if ((evt.x >= modalRect.left && evt.x <= modalRect.right)
            && (evt.y >= modalRect.top && evt.y <= modalRect.bottom))
        {
            return;
        }
        hideDialog();
    }

    const dialogClass = computed<string[]>(() =>{
        return props.isModal ? ["modal-dialog-position", "standard-dialog"] : ["non-modal-dialog-position", "standard-dialog"];
    });

    function getCurrentDialogPos(): ICartesianCoord | undefined
    {
        const dialog = theDialog.value;
        if (!dialog)
        {
            alert("dialog is undefined");
            return;
        }

        if (!dialog.style.left)
        {
            return;
        }

        // Get the current position
        const currentPosLeft = +dialog.style.left.replace("px", "");
        const currentPosTop = +dialog.style.top.replace("px", "");

        return { X: currentPosLeft, Y: currentPosTop };
    }

    function getHighestZIndex(): number
    {
        const elements = document.querySelectorAll('*'); // Select all elements
        let highest = 0;
        for (const ele of elements) {
            var zIndex = document.defaultView?.getComputedStyle(ele).getPropertyValue('z-index');
            if (!zIndex)
            {
                continue;
            }

            if (zIndex !== 'auto' && parseInt(zIndex) > highest) 
            {
                highest = parseInt(zIndex);
            }
        }
        return highest;
    }    

    const dialogStyle = computed(()=>{
        return {
            width: cssValue(props.width),
            height: cssValue(props.height),
            "z-index": (1 + highestZIndex.value)
        }
    });

    const footerClasses = computed<string>(() =>{
        if (props.footerAlignEnd)
            return "sidePadding text-end";
        return "sidePadding";
    });

    function setModalPosition(leftInPx?: number, topInPx?: number)
    {
        const dialog = theDialog.value;
        if (!dialog)
        {
            alert("Invalid dialog");
            return;
        }

        if (undefined !== leftInPx)
            dialog.style.left = leftInPx + 'px';
        if (undefined !== topInPx)
            dialog.style.top = topInPx + 'px';        
    }
    const draggableClass = computed<string>(() =>{
        if(!props.allowDragging)
            return "flex-fill";
        return "flex-fill draggable-container";
    });
</script>

<style scoped>
    .standard-dialog {
        background-color: white;
        border-radius: 4px;
        border-width: 1px;
        box-shadow: 4px 4px 20px 2px;
        animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .modal-dialog-position {
        position: fixed;
    }

    .non-modal-dialog-position {
        position: absolute;
    }
    
  
    dialog::backdrop {
        opacity: 0.5;
        background-color:black;
    }

    .dialogClose {
        cursor: pointer;
    }

    .sectionBreak {
        margin-left:-16px !important; 
        margin-right:-16px !important;
    }

    .draggable-container {
        cursor: move;
      }
</style>


<template>
    <dialog ref="theDialog" :class="dialogClass" v-if="props.show" :style="dialogStyle">
        <form>
            <div ref="titleDiv" @mousedown="evt => onTitleMouseDown(evt)">
                <h5 class="modal-title">
                    <div class="d-flex">
                        <div :class="draggableClass">
                            <slot name="title">Modal</slot>
                        </div>
                        <div class="d-flex flex-fill justify-content-end">
                            <Icon v-if="props.showCloseButton"
                                    class="dialogClose" 
                                    :icon="IconTypes.Close" 
                                    @click="hideDialog()" 
                                    :displayStyle="DisplayStyle.Primary" 
                                    :title="props.closeIconTitle" 
                                    :hoverCursor="HoverCursorTypes.Pointer"/>
                        </div>
                    </div>
                </h5>
            </div>            
        </form>
        <hr class="sectionBreak" v-if="showSectionBreaks"/>

        <div>
            <slot></slot>
        </div>

        <div v-if="hasFooter" :class="footerClasses">
            <hr class="sectionBreak" v-if="showSectionBreaks"/>
            <slot name="footer"></slot>
        </div>
      </dialog>    
</template>
